/**
 * Servicios HTTP para **órdenes de venta (OV)**.
 *
 * - Listado paginado (cabeceras, liviano): `GET sales-orders/headers`
 * - Búsqueda con filtros: `GET sales-orders/search`
 * - Detalle por **id de recurso**: `GET sales-orders/:id` (misma clave que cancelación).
 * - Detalle por número de orden negocio: `GET sales-orders/order-number/:orderNumber` (alternativa).
 *
 * **Cancelación:** `PUT sales-orders/{id}` con cuerpo `{ "status": "CANCELLED" }` y opcional `modifiedBy` (UUID usuario).
 * **Auditoría post-SAP:** tras envío exitoso a SAP, `PUT sales-orders/{id}` con `{ "modifiedBy" }` (`updateSalesOrderModifiedBy`).
 * (`updateSalesOrderStatus`, `cancelSalesOrdersByIds`, `updateSalesOrderModifiedBy`).
 *
 * **Quién consume este módulo:** `SalesOrderList.js` (listado, búsqueda, envío masivo a SAP, cancelación masiva,
 * carga mayorista solo usa el import aparte), `SalesOrderDetail.js` (detalle + stepper).
 * **Listado:** `mapSalesOrderApiToRow` expone `brand`, `productLineCount`, `productUnitsTotal`, `totalUnitsByOcReference`
 * si el API los envía (o líneas embebidas `lines`/`items`/…) para columnas y navegación al detalle.
 *
 * **Estado en listado / detalle (columna `status` del API):** `CANCELLED` → cancelada; `PENDING` → creada; `ACCEPTED` → aceptada en SAP
 * (pill «Enviada a SAP» + DocNum vía `docNumberReference` / `externalOrderId`); `SHIPPED` → despachada; `PARTIALLY_SHIPPED` → despacho parcial;
 * `CLOSED` → cerrada (sin completar despacho); `RETURNED` → devuelta. Heurística `external_order_id` vs OC no clasifica el estado visible.
 * Heurística previa `external_order_id` vs prefijo OC (`isSalesOrderLikelySentToSap`) ya no clasifica el estado visible.
 *
 * El token Bearer lo inyecta `axiosInstance`; aquí solo se arman URLs, query params y se normaliza el JSON
 * (`unwrapListPayload`, `unwrapDetailPayload`, mapeos a filas de tabla / pantalla de detalle).
 */
import axiosInstance from '../utils/axiosInstance';
import { formatSalesOrderDisplayDateEsCl } from '../utils/salesOrderListDateDisplay';

/** Fecha corta es-CL para resumen del detalle OV (misma lógica visual que el listado). */
function formatDetailDateCl(value) {
  return formatSalesOrderDisplayDateEsCl(value, '—');
}

/** Valores `status` en query `sales-orders/search` (español minúsculas, según contrato ejemplo). */
export const SALES_ORDER_STATUS_UI_TO_API = {
  Pending: 'PENDING',
  Accepted: 'ACCEPTED',
  Canceled: 'CANCELLED',
  Shipped: 'SHIPPED',
  PartiallyShipped: 'PARTIALLY_SHIPPED',
  Returned: 'RETURNED',
  Closed: 'CLOSED'
};

/**
 * Valor del query `channel` en `sales-orders/search`: debe coincidir con el **nombre** del canal en catálogo
 * (`GET sale-channels`). En `SalesOrderList` el desplegable usa esos nombres; este mapa solo aplica como
 * respaldo si falla la carga del catálogo (opciones legacy por slug).
 * OV antiguas con canal numérico (`1`…) dependen de que el API normalice o de migración de datos.
 */
export const SALES_ORDER_CHANNEL_UI_TO_API = {
  mayorista: 'Mayorista',
  multimarca: 'Multimarca',
  ixcomercio: 'IxComercio',
  retail: 'Retail',
  b2cdirecto: 'B2C Directo'
};

/**
 * Convierte un valor de fecha del API (ISO, epoch s/ms, array tipo Java LocalDateTime, objeto anidado) a string estable para el front.
 * @param {unknown} value
 * @returns {string}
 */
function normalizeListDateInput(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value > 0 && value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
  }
  if (Array.isArray(value) && value.length >= 3) {
    const y = value[0];
    const mo = value[1] ?? 1;
    const day = value[2] ?? 1;
    const h = value[3] ?? 0;
    const mi = value[4] ?? 0;
    const s = value[5] ?? 0;
    const nano = value[6] ?? 0;
    const fracMs = typeof nano === 'number' ? Math.floor(nano / 1e6) : 0;
    const t = Date.UTC(y, mo - 1, day, h, mi, s, fracMs);
    if (Number.isNaN(t)) return '';
    return new Date(t).toISOString();
  }
  if (typeof value === 'string') {
    const t = value.trim();
    return t;
  }
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    if (value.date != null) return normalizeListDateInput(value.date);
    if (value.year != null && value.month != null && value.day != null) {
      return normalizeListDateInput([value.year, value.month, value.day]);
    }
  }
  return '';
}

/** Prueba varios campos hasta obtener una fecha no vacía (listado OV). */
function firstNonEmptyDate(...candidates) {
  for (const c of candidates) {
    const n = normalizeListDateInput(c);
    if (n !== '') return n;
  }
  return '';
}

/**
 * Etiqueta de moneda en UI para listado/detalle OV.
 * Perú: se usa el símbolo **S/** (convención local); Chile y resto: código API o CLP.
 * @param {unknown} marketplace
 * @param {unknown} marketplaceLabel
 * @param {unknown} apiCurrency
 * @returns {string}
 */
export function inferSalesOrderCurrencyFromMarketplace(marketplace, marketplaceLabel, apiCurrency) {
  const fromApi =
    apiCurrency != null && String(apiCurrency).trim() !== '' ? String(apiCurrency).trim().toUpperCase() : '';
  const combined = `${String(marketplace ?? '')} ${String(marketplaceLabel ?? '')}`
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
  if (combined.includes('falabellaperu') || /\bfalabella peru\b/.test(combined)) {
    return 'S/';
  }
  if (combined.includes('ripleyperu') || /\bripley peru\b/.test(combined)) {
    return 'S/';
  }
  if (combined.includes('oechsle')) {
    return 'S/';
  }
  if (combined.includes('ixcomercioperu') || /\bix comercio peru\b/.test(combined)) {
    return 'S/';
  }
  if (combined.includes('multimarcaperu') || /\bmultimarca peru\b/.test(combined)) {
    return 'S/';
  }
  /** OV peruanas guardan `currency = "SOL"` (código SAP); algunos flujos usan `PEN`. */
  if (fromApi === 'SOL' || fromApi === 'PEN') {
    return 'S/';
  }
  if (combined.includes('paris') || combined.includes('falabella') || combined.includes('ripley')) {
    return fromApi || 'CLP';
  }
  return fromApi || 'CLP';
}

/**
 * `true` si la OV se trata como **Perú** (misma heurística que moneda `S/` en listado).
 * Útil para columnas de moneda en listado/detalle; el envío masivo a SAP usa vista previa con stock para todas las OV.
 *
 * @param {unknown} marketplace
 * @param {unknown} marketplaceLabel
 * @param {unknown} apiCurrency
 * @returns {boolean}
 */
export function isSalesOrderPeruMarketplace(marketplace, marketplaceLabel, apiCurrency) {
  return inferSalesOrderCurrencyFromMarketplace(marketplace, marketplaceLabel, apiCurrency) === 'S/';
}


/**
 * DocNum SAP (u otro id externo) desde raíz del ítem/API y `header` / `meta`.
 * @param {object|null|undefined} root
 * @param {object|null|undefined} header
 * @param {object|null|undefined} meta
 * @returns {string}
 */
function pickExternalOrderId(root, header, meta) {
  if (!root || typeof root !== 'object') return '';
  const raw =
    root.external_order_id ??
    root.externalOrderId ??
    header?.external_order_id ??
    header?.externalOrderId ??
    meta?.external_order_id ??
    meta?.externalOrderId ??
    '';
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  return s;
}

/**
 * DocNum SAP mostrable al usuario (`docNumberReference` en API); distinto de `externalOrderId` (DocEntry u otro legado).
 *
 * @param {object|null|undefined} root
 * @param {object|null|undefined} header
 * @param {object|null|undefined} meta
 * @returns {string}
 */
function pickDocNumberReference(root, header, meta) {
  if (!root || typeof root !== 'object') return '';
  const raw =
    root.docNumberReference ??
    root.doc_number_reference ??
    header?.docNumberReference ??
    header?.doc_number_reference ??
    meta?.docNumberReference ??
    meta?.doc_number_reference ??
    '';
  if (raw == null || raw === '') return '';
  return String(raw).trim();
}

/**
 * Texto DocNum SAP para etiquetas «Enviada a SAP» / stepper: prioriza `docNumberReference`, si no hay usa `externalOrderId`.
 *
 * @param {{ docNumberReference?: string, doc_number_reference?: string, externalOrderId?: string }|null|undefined} row
 * @returns {string}
 */
export function getSalesOrderSapDocNumForDisplay(row) {
  if (!row || typeof row !== 'object') return '';
  const dn = row.docNumberReference ?? row.doc_number_reference;
  if (dn != null && String(dn).trim() !== '') return String(dn).trim();
  return String(row.externalOrderId ?? '').trim();
}

/**
 * Parte **OC** del número de orden de negocio: todo lo que va **antes del último** `-`
 * (el backend concatena `OC + '-' + código sucursal`).
 *
 * @param {unknown} orderNumber - ej. `417098-100`, `2311737-10095`
 * @returns {string|null} `null` si no hay guion utilizable (no se aplica heurística “OC duplicada en external_order_id”).
 */
export function orderNumberOcPrefix(orderNumber) {
  const s = String(orderNumber ?? '').trim();
  const i = s.lastIndexOf('-');
  if (i <= 0) return null;
  const prefix = s.slice(0, i).trim();
  return prefix || null;
}

/**
 * Suma `quantity` de líneas ya mapeadas al shape de detalle OV (`mapSalesOrderDetailApiToUi`).
 *
 * @param {Array<{ quantity?: number|string }>|null|undefined} lines
 * @returns {number}
 */
export function sumSalesOrderDetailLineQuantities(lines) {
  if (!Array.isArray(lines) || lines.length === 0) return 0;
  return lines.reduce((sum, line) => {
    const q = line?.quantity;
    if (typeof q === 'number' && Number.isFinite(q)) return sum + q;
    const n = parseFloat(String(q ?? '').replace(',', '.'), 10);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}

/**
 * Normaliza cantidad numérica de línea de detalle OV (enviadas / retornadas): null → 0, entero ≥ 0.
 *
 * @param {unknown} raw
 * @returns {number}
 */
function parseSalesOrderDetailShipReturnQty(raw) {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(0, Math.round(raw));
  const n = parseFloat(String(raw).replace(',', '.'), 10);
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
}

/** Cantidad en quiebre (`broke_quantity`); puede ser decimal en BD o objeto tipo BigDecimal en JSON. */
function parseSalesOrderDetailBrokeQty(raw) {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(0, raw);
  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    const coerced = coerceLineMoneyToNumber(raw);
    if (coerced != null && Number.isFinite(coerced)) return Math.max(0, coerced);
  }
  const n = parseFloat(String(raw).replace(',', '.'), 10);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

/**
 * Suma `shippedQuantity` de líneas ya mapeadas (`mapSalesOrderDetailApiToUi`).
 *
 * @param {Array<{ shippedQuantity?: number }>|null|undefined} lines
 * @returns {number}
 */
export function sumSalesOrderDetailShippedQuantities(lines) {
  if (!Array.isArray(lines) || lines.length === 0) return 0;
  return lines.reduce((sum, line) => {
    const q = line?.shippedQuantity;
    if (typeof q === 'number' && Number.isFinite(q)) return sum + Math.max(0, Math.round(q));
    return sum;
  }, 0);
}

/**
 * Suma `returnedQuantity` de líneas ya mapeadas (`mapSalesOrderDetailApiToUi`).
 *
 * @param {Array<{ returnedQuantity?: number }>|null|undefined} lines
 * @returns {number}
 */
export function sumSalesOrderDetailReturnedQuantities(lines) {
  if (!Array.isArray(lines) || lines.length === 0) return 0;
  return lines.reduce((sum, line) => {
    const q = line?.returnedQuantity;
    if (typeof q === 'number' && Number.isFinite(q)) return sum + Math.max(0, Math.round(q));
    return sum;
  }, 0);
}

/**
 * `true` si `externalOrderId` parece **DocNum / id SAP** y no la OC repetida en `external_order_id`.
 * Cuando el prefijo OC coincide con `externalOrderId`, el backend aún no sustituyó por el DocNum real.
 *
 * @param {unknown} externalOrderId
 * @param {unknown} orderNumber
 * @returns {boolean}
 */
export function isSalesOrderLikelySentToSap(externalOrderId, orderNumber) {
  const ext = String(externalOrderId ?? '').trim();
  if (!ext) return false;
  const prefix = orderNumberOcPrefix(orderNumber);
  if (prefix == null) return true;
  return ext !== prefix;
}

/**
 * `true` si el `status` del API indica OV cancelada (comparación case-insensitive).
 *
 * @param {unknown} status
 * @returns {boolean}
 */
export function isSalesOrderStatusCanceled(status) {
  return String(status ?? '').trim().toUpperCase() === 'CANCELLED';
}

/**
 * `true` si el `status` del API indica OV aceptada en SAP (comparación case-insensitive).
 *
 * @param {unknown} status
 * @returns {boolean}
 */
export function isSalesOrderStatusAccepted(status) {
  return String(status ?? '').trim().toUpperCase() === 'ACCEPTED';
}

/**
 * `true` si el `status` del API es PENDING (envío a SAP y selección en listado; comparación case-insensitive).
 *
 * @param {unknown} status
 * @returns {boolean}
 */
export function isSalesOrderStatusPending(status) {
  return String(status ?? '').trim().toUpperCase() === 'PENDING';
}

/**
 * `true` si el `status` del API es SHIPPED (comparación case-insensitive).
 *
 * @param {unknown} status
 * @returns {boolean}
 */
export function isSalesOrderStatusShipped(status) {
  return String(status ?? '').trim().toUpperCase() === 'SHIPPED';
}

/**
 * `true` si el `status` del API es PARTIALLY_SHIPPED (comparación case-insensitive).
 *
 * @param {unknown} status
 * @returns {boolean}
 */
export function isSalesOrderStatusPartiallyShipped(status) {
  return String(status ?? '').trim().toUpperCase() === 'PARTIALLY_SHIPPED';
}

/**
 * `true` si el `status` del API es RETURNED (comparación case-insensitive).
 *
 * @param {unknown} status
 * @returns {boolean}
 */
export function isSalesOrderStatusReturned(status) {
  return String(status ?? '').trim().toUpperCase() === 'RETURNED';
}

/**
 * `true` si el `status` del API es CLOSED (comparación case-insensitive).
 *
 * @param {unknown} status
 * @returns {boolean}
 */
export function isSalesOrderStatusClosed(status) {
  return String(status ?? '').trim().toUpperCase() === 'CLOSED';
}

/**
 * `true` si la OV puede cancelarse desde UI (solo `PENDING` o `ACCEPTED`).
 *
 * @param {unknown} status
 * @returns {boolean}
 */
export function canCancelSalesOrderStatus(status) {
  return isSalesOrderStatusPending(status) || isSalesOrderStatusAccepted(status);
}

/**
 * `true` si la OV puede marcarse en el listado masivo (PENDING o ACCEPTED; no cancelada).
 *
 * @param {unknown} status
 * @returns {boolean}
 */
export function isSalesOrderStatusSelectableInList(status) {
  return isSalesOrderStatusPending(status) || isSalesOrderStatusAccepted(status);
}

/**
 * `status` normalizado a string desde cuerpos de listado, detalle o carga cruda (`GET` OV).
 * @param {object|null|undefined} api
 * @returns {string}
 */
export function pickSalesOrderStatusFromApiPayload(api) {
  if (!api || typeof api !== 'object') return '';
  const s = api.status ?? api.orderStatus ?? api.order_status;
  if (s == null) return '';
  return typeof s === 'string' ? s : String(s);
}

/**
 * Clasificación del estado visible en listado / export según **solo** la columna `status` del API:
 * orden: `CANCELLED` → `SHIPPED` → `PARTIALLY_SHIPPED` → `CLOSED` → `RETURNED` → `ACCEPTED` → en otro caso (`PENDING`) → creada.
 *
 * @param {{ status?: string }|null|undefined} row
 * @returns {'canceled'|'shipped'|'partially_shipped'|'closed'|'returned'|'sent_sap'|'created'}
 */
export function getSalesOrderListStatusKind(row) {
  if (!row || typeof row !== 'object') return 'created';
  if (isSalesOrderStatusCanceled(row.status)) return 'canceled';
  if (isSalesOrderStatusShipped(row.status)) return 'shipped';
  if (isSalesOrderStatusPartiallyShipped(row.status)) return 'partially_shipped';
  if (isSalesOrderStatusClosed(row.status)) return 'closed';
  if (isSalesOrderStatusReturned(row.status)) return 'returned';
  if (isSalesOrderStatusAccepted(row.status)) return 'sent_sap';
  return 'created';
}

/**
 * Convierte `workflowCurrentStep` del API (escala histórica 1–8) al índice del stepper de detalle OV de 5 pasos.
 *
 * @param {unknown} workflowCurrentStep
 * @returns {number} Entero 1–5
 */
export function mapSalesOrderWorkflowApiStepToFiveSteps(workflowCurrentStep) {
  const n = Math.round(Number(workflowCurrentStep));
  const step = !Number.isFinite(n) || n < 1 ? 1 : Math.min(8, Math.max(1, n));
  if (step <= 1) return 1;
  if (step === 2) return 2;
  if (step <= 5) return 3;
  if (step === 6) return 4;
  return 5;
}

/**
 * Paso activo (1–5) del stepper de detalle según clasificación de estado y paso workflow del API.
 *
 * @param {'canceled'|'shipped'|'partially_shipped'|'closed'|'returned'|'sent_sap'|'created'} kind
 * @param {unknown} workflowCurrentStep
 * @returns {number}
 */
export function resolveSalesOrderDetailWorkflowActiveStep(kind, workflowCurrentStep) {
  const mapped = mapSalesOrderWorkflowApiStepToFiveSteps(workflowCurrentStep);
  if (kind === 'canceled') return 1;
  if (kind === 'created') return 1;
  if (kind === 'sent_sap') return Math.max(2, mapped);
  if (kind === 'shipped' || kind === 'partially_shipped' || kind === 'closed' || kind === 'returned') {
    return Math.max(3, mapped);
  }
  return mapped;
}

/** @param {unknown} v @returns {number|null} */
function parseOptionalNumber(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const n = parseFloat(String(v).replace(',', '.'), 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Total de unidades de todas las OV con la misma OC de origen (`totalUnitsByOcReference` en API).
 * Toleran raíz, `header`, `meta` y `shipping` (detalle).
 *
 * @param {object|null|undefined} root
 * @param {object|null|undefined} header
 * @param {object|null|undefined} meta
 * @param {object|null|undefined} shipping
 * @returns {number|null}
 */
function parseTotalUnitsByOcReferenceFromApi(root, header, meta, shipping) {
  const totalUnitsByOcRefRaw =
    root?.totalUnitsByOcReference ??
    root?.total_units_by_oc_reference ??
    root?.purchaseOrderTotalUnits ??
    root?.purchase_order_total_units ??
    header?.totalUnitsByOcReference ??
    header?.total_units_by_oc_reference ??
    meta?.totalUnitsByOcReference ??
    meta?.total_units_by_oc_reference ??
    shipping?.totalUnitsByOcReference ??
    shipping?.total_units_by_oc_reference ??
    null;
  if (typeof totalUnitsByOcRefRaw === 'number' && Number.isFinite(totalUnitsByOcRefRaw)) {
    return totalUnitsByOcRefRaw;
  }
  if (totalUnitsByOcRefRaw != null && totalUnitsByOcRefRaw !== '') {
    const n = parseFloat(String(totalUnitsByOcRefRaw).replace(',', '.'), 10);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Líneas embebidas en un ítem de listado OV (si el API las envía).
 * @param {object} item
 * @returns {object[]}
 */
function pickListItemLinesArray(item) {
  if (!item || typeof item !== 'object') return [];
  const header = item.header && typeof item.header === 'object' ? item.header : null;
  const meta = item.meta && typeof item.meta === 'object' ? item.meta : null;
  const candidates = [
    item.lines,
    item.items,
    item.orderLines,
    item.order_lines,
    item.details,
    item.orderDetails,
    item.order_details,
    header?.lines,
    header?.items,
    meta?.lines
  ];
  for (const c of candidates) {
    if (Array.isArray(c) && c.length > 0) return c;
  }
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
}

/**
 * Suma cantidades en líneas crudas de listado (mismos alias que detalle en lo posible).
 * @param {object[]} lines
 * @returns {number|null} `null` si no hay líneas o ninguna cantidad parseable.
 */
function sumListRawLineQuantities(lines) {
  if (!Array.isArray(lines) || lines.length === 0) return null;
  let sum = 0;
  let any = false;
  for (const l of lines) {
    const qRaw = l?.quantity ?? l?.qty ?? l?.cantidad ?? l?.orderedQuantity ?? l?.ordered_quantity;
    if (qRaw == null && qRaw !== 0) continue;
    const q =
      typeof qRaw === 'number' && Number.isFinite(qRaw)
        ? qRaw
        : parseFloat(String(qRaw).replace(',', '.'), 10);
    if (Number.isFinite(q)) {
      sum += q;
      any = true;
    }
  }
  return any ? sum : null;
}

/** Normaliza texto para comparar marca vs tipo de venta (sin tildes, minúsculas). */
function normalizeComparableLabel(value) {
  return String(value ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/** Valores del select «Tipo de venta» en carga mayorista: no deben mostrarse como columna Marca. */
const WHOLESALE_SALE_TYPE_LABELS = new Set(
  ['venta', 'consignacion', 'traslado'].map((s) => normalizeComparableLabel(s))
);

/**
 * Marca comercial de la OV (nombres alineados con `GET brandings`), **no** `tipovta` / tipo de venta.
 * El import envía `marca` en query por nombre de marca; si el API devuelve tipo de venta en `marca`, se descarta.
 */
function pickBrandForListRow(item, header, meta) {
  const saleTypeRaw =
    item.tipovta ??
    item.tipoVenta ??
    item.tipo_venta ??
    item.saleTypeDesc ??
    item.sale_type_desc ??
    item.orderType ??
    item.order_type ??
    item.saleType ??
    item.sale_type ??
    header?.tipovta ??
    header?.tipoVenta ??
    header?.saleTypeDesc ??
    header?.sale_type_desc ??
    meta?.tipovta ??
    meta?.saleTypeDesc ??
    meta?.sale_type_desc ??
    '';
  const saleTypeNorm = normalizeComparableLabel(saleTypeRaw);

  /** Campos explícitos de marca del API: se muestran tal cual salvo que sean literalmente «Venta/Consignación/Traslado». */
  const tryExplicitBrand = (raw) => {
    if (raw == null || raw === '') return null;
    const s = typeof raw === 'string' ? raw.trim() : String(raw).trim();
    if (!s) return null;
    const sNorm = normalizeComparableLabel(s);
    if (WHOLESALE_SALE_TYPE_LABELS.has(sNorm)) return null;
    return s;
  };

  const tryCandidate = (raw) => {
    if (raw == null || raw === '') return null;
    const s = typeof raw === 'string' ? raw.trim() : String(raw).trim();
    if (!s) return null;
    const sNorm = normalizeComparableLabel(s);
    if (WHOLESALE_SALE_TYPE_LABELS.has(sNorm)) return null;
    if (saleTypeNorm && sNorm === saleTypeNorm) return null;
    return s;
  };

  const explicitBrandSources = [
    item.brand,
    item.Brand,
    item.brandName,
    item.brand_name,
    header?.brand,
    header?.Brand,
    header?.brandName,
    meta?.brand,
    meta?.Brand,
    meta?.brandName
  ];
  for (const c of explicitBrandSources) {
    const picked = tryExplicitBrand(c);
    if (picked) return picked;
  }

  const candidates = [
    item.productBrand,
    item.product_brand,
    item.marcaNombre,
    item.marca_nombre,
    item.marcaProducto,
    item.marca_producto,
    item.nombreMarca,
    item.nombre_marca,
    item.wholesaleBrand,
    item.wholesale_brand,
    item.importMarca,
    item.import_marca,
    item.marcaCabecera,
    item.marca_cabecera,
    item.marcaNegocio,
    item.marca_negocio,
    item.customerBrand,
    item.customer_brand,
    item.clientBrand,
    item.client_brand,
    item.detalleMarca,
    item.detalle_marca,
    item.labelMarca,
    item.label_marca,
    header?.customerBrand,
    header?.clientBrand,
    header?.marcaNombre,
    header?.productBrand,
    meta?.marcaNombre,
    meta?.productBrand,
    item.marca,
    header?.marca,
    meta?.marca
  ];
  for (const c of candidates) {
    const picked = tryCandidate(c);
    if (picked) return picked;
  }
  return '';
}

/**
 * `true` si el valor tiene forma de UUID estándar (segmento de ruta de `GET sales-orders/:id` en Spring).
 * Los números de orden tipo `98888902-20021` no cumplen el patrón 8-4-4-4-12.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isSalesOrderUuidKey(value) {
  const s = value != null ? String(value).trim() : '';
  if (!s) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

/**
 * Sucursal / dirección de entrega para listado y export (p. ej. MULTIMARCA `shipping_address`).
 * @param {object} item
 * @param {object|null} header
 * @param {object|null} meta
 * @param {object|null} shippingBlock
 * @returns {string}
 */
export function pickShippingDestinationForListRow(item, header, meta, shippingBlock) {
  if (!item || typeof item !== 'object') return '';
  const v =
    item.shippingAddress ??
    item.shipping_address ??
    header?.shippingAddress ??
    header?.shipping_address ??
    meta?.shippingAddress ??
    meta?.shipping_address ??
    (shippingBlock && typeof shippingBlock === 'object'
      ? shippingBlock.address ??
        shippingBlock.shippingAddress ??
        shippingBlock.shipping_address
      : '') ??
    '';
  return String(v).trim();
}

/**
 * Columna «Sucursal Destino»: prioriza `clientName` (`customer_name`); si no hay, `shippingDestination`.
 * @param {{ shippingDestination?: string, clientName?: string }} row
 * @returns {string}
 */
export function formatSalesOrderListDestination(row) {
  if (!row || typeof row !== 'object') return '—';
  const client = String(row.clientName ?? '').trim();
  if (client) return client;
  const ship = String(row.shippingDestination ?? '').trim();
  return ship || '—';
}

/**
 * Normaliza un ítem de API a la forma usada por la tabla en `SalesOrderList`.
 * Tolera snake_case y nombres alternativos.
 */
export function mapSalesOrderApiToRow(item) {
  if (!item || typeof item !== 'object') return null;

  const id = item.id ?? item.salesOrderId ?? item.orderId ?? item.orderNumber ?? '';
  const rawStatus = item.status ?? item.orderStatus ?? 'Pending';
  const status = typeof rawStatus === 'string' ? rawStatus : String(rawStatus);
  const statusLabel = item.statusLabel ?? item.statusDescription ?? status;

  const rawPay = (item.paymentStatus ?? 'pending');
  const paymentStatus = String(rawPay).toLowerCase();
  const normalizedPay = ['paid', 'pending', 'canceled'].includes(paymentStatus)
    ? paymentStatus
    : 'pending';

  const marketplace = item.marketplace ?? item.marketPlace ?? '';
  const marketplaceNorm = String(marketplace)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '');
  let marketplaceLabel =
    item.marketplaceLabel ?? item.marketplaceName ?? item.marketplace_label ?? marketplace;
  if (marketplaceNorm === 'multimarca') {
    const rawLabel = String(marketplaceLabel || '').trim();
    if (!rawLabel || rawLabel.toUpperCase() === 'MULTIMARCA') {
      marketplaceLabel = 'Multimarca';
    }
  }
  if (marketplaceNorm === 'ixcomercioperu') {
    const rawLabel = String(marketplaceLabel || '').trim();
    if (!rawLabel || rawLabel.toUpperCase() === 'IXCOMERCIOPERU') {
      marketplaceLabel = 'IX Comercio Perú';
    }
  }
  if (marketplaceNorm === 'multimarcaperu') {
    const rawLabel = String(marketplaceLabel || '').trim();
    if (!rawLabel || rawLabel.toUpperCase() === 'MULTIMARCAPERU') {
      marketplaceLabel = 'Multimarca Perú';
    }
  }

  const paymentStatusLabel =
    item.paymentStatusLabel ??
    item.payment_status_label ??
    (normalizedPay === 'paid' ? 'Pagado' : normalizedPay === 'canceled' ? 'Anulado' : 'Pendiente');

  const header = item.header && typeof item.header === 'object' ? item.header : null;
  const meta = item.meta && typeof item.meta === 'object' ? item.meta : null;
  const externalOrderId = pickExternalOrderId(item, header, meta);
  const docNumberReference = pickDocNumberReference(item, header, meta);

  const listLines = pickListItemLinesArray(item);
  const fromLinesCount = listLines.length;
  const fromLinesQtySum = sumListRawLineQuantities(listLines);

  const productLineCountRaw =
    item.totalLines ??
    item.total_lines ??
    header?.totalLines ??
    header?.total_lines ??
    meta?.totalLines ??
    meta?.total_lines ??
    item.productLineCount ??
    item.product_line_count ??
    item.linesCount ??
    item.lines_count ??
    item.lineCount ??
    item.line_count ??
    item.numberOfLines ??
    item.number_of_lines ??
    item.itemsCount ??
    item.items_count ??
    header?.productLineCount ??
    header?.linesCount ??
    meta?.productLineCount ??
    meta?.linesCount;
  let productLineCount = parseOptionalNumber(productLineCountRaw);
  if (productLineCount == null && fromLinesCount > 0) {
    productLineCount = fromLinesCount;
  }

  const productUnitsTotalRaw =
    item.totalUnits ??
    item.total_units ??
    header?.totalUnits ??
    header?.total_units ??
    meta?.totalUnits ??
    meta?.total_units ??
    item.productUnitsTotal ??
    item.product_units_total ??
    item.totalLineQuantity ??
    item.total_line_quantity ??
    item.totalQuantity ??
    item.total_quantity ??
    item.sumQuantity ??
    item.sum_quantity;
  let productUnitsTotal = parseOptionalNumber(productUnitsTotalRaw);
  if (productUnitsTotal == null && fromLinesQtySum != null) {
    productUnitsTotal = fromLinesQtySum;
  }

  const brand = pickBrandForListRow(item, header, meta);
  const shippingBlock = item.shipping && typeof item.shipping === 'object' ? item.shipping : null;
  const totalUnitsByOcReference = parseTotalUnitsByOcReferenceFromApi(item, header, meta, shippingBlock);

  return {
    id,
    orderNumber: item.orderNumber ?? item.order_number ?? '',
    createdAt: firstNonEmptyDate(
      item.createdAt,
      item.created_at,
      item.creationDate,
      item.creation_date,
      item.fechaCreacion,
      item.fecha_creacion,
      item.fechaRegistro,
      item.fecha_registro,
      item.registeredAt,
      item.registered_at,
      item.dateCreated,
      item.date_created,
      header?.createdAt,
      header?.fechaCreacion,
      meta?.createdAt
    ),
    /** Fecha de la orden de compra (cliente); en search/list el backend suele exponerla como `shippedDate`. */
    purchaseOrderDate: firstNonEmptyDate(
      item.shippedDate,
      item.shipped_date,
      item.ShippedDate,
      header?.shippedDate,
      header?.shipped_date,
      meta?.shippedDate,
      item.purchaseOrderDate,
      item.purchase_order_date,
      item.orderDate,
      item.order_date,
      item.poDate,
      item.po_date,
      item.customerOrderDate,
      item.customer_order_date,
      item.fechaOc,
      item.fecha_oc,
      item.fechaOC,
      item.fechaPedido,
      item.fecha_pedido,
      item.ocDate,
      item.oc_date,
      item.documentDate,
      item.document_date,
      header?.purchaseOrderDate,
      header?.orderDate,
      header?.fechaOc,
      meta?.purchaseOrderDate,
      meta?.orderDate
    ),
    estimatedDeliveryDate: firstNonEmptyDate(
      item.estimatedDeliveryDate,
      item.estimated_delivery_date,
      item.deliveryDate,
      item.delivery_date,
      item.fechaEntrega,
      item.fecha_entrega,
      item.fechaEntregaEstimada,
      item.fecha_entrega_estimada,
      item.fechaEntregaEst,
      item.shipDate,
      item.ship_date,
      item.expectedDeliveryDate,
      item.expected_delivery_date,
      header?.estimatedDeliveryDate,
      header?.fechaEntrega,
      meta?.estimatedDeliveryDate
    ),
    channel: item.channel ?? item.salesChannel ?? item.sales_channel ?? '',
    marketplace: String(marketplace).toLowerCase(),
    marketplaceLabel,
    clientName:
      item.clientName ?? item.client_name ?? item.customerName ?? item.customer_name ?? '',
    /** Sucursal / dirección de entrega (MULTIMARCA: distinto de razón social en `clientName`). */
    shippingDestination: pickShippingDestinationForListRow(item, header, meta, shippingBlock),
    clientRut: item.clientRut ?? item.client_rut ?? item.customerRut ?? item.customer_rut ?? '',
    total: item.total ?? item.totalAmount ?? item.total_formatted ?? '',
    currency: inferSalesOrderCurrencyFromMarketplace(marketplace, marketplaceLabel, item.currency),
    shippingCity: item.shippingCity ?? item.shipping_city ?? '',
    paymentStatus: normalizedPay,
    paymentStatusLabel,
    status,
    statusLabel,
    /** DocEntry / id externo legado (`external_order_id`). */
    externalOrderId,
    /** DocNum SAP para mostrar al usuario (columna Estado). */
    docNumberReference,
    /** Marca informada al crear la OV (import mayorista, etc.). */
    brand: brand || '',
    /** Cantidad de líneas de producto; el API puede enviar agregados o líneas embebidas en el listado. */
    productLineCount: productLineCount != null && Number.isFinite(productLineCount) ? productLineCount : null,
    /** Suma de cantidades pedidas en líneas. */
    productUnitsTotal: productUnitsTotal != null && Number.isFinite(productUnitsTotal) ? productUnitsTotal : null,
    /** Unidades totales de la OC de referencia (mismo prefijo `orderNumber`); listado liviano y detalle. */
    totalUnitsByOcReference
  };
}

/** @param {unknown} v @returns {number|undefined} */
function coerceFiniteNumberLoose(v) {
  if (v == null || v === '') return undefined;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const n = Number.parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Unifica metadatos de paginación (incl. inferir `totalPages` desde `totalElements` y `size`).
 *
 * @param {object|null|undefined} payload - respuesta HTTP cruda (para leer metadatos en el mismo nivel que `data`).
 * @param {{ rows?: object[], totalPages?: unknown, totalElements?: unknown, last?: unknown, size?: unknown, number?: unknown }} partial
 */
function finalizeListPayload(payload, partial) {
  const rows = partial.rows || [];
  const top = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};

  let totalPages = partial.totalPages ?? top.totalPages ?? top.total_pages ?? top.pageCount;
  let totalElements =
    partial.totalElements ??
    top.totalElements ??
    top.total_elements ??
    top.totalRecords ??
    top.total_records;
  let last = partial.last ?? top.last ?? top.isLastPage;

  const pageSize =
    coerceFiniteNumberLoose(partial.size) ??
    coerceFiniteNumberLoose(top.size) ??
    coerceFiniteNumberLoose(top.pageSize) ??
    coerceFiniteNumberLoose(top.limit);

  totalPages = coerceFiniteNumberLoose(totalPages);
  totalElements = coerceFiniteNumberLoose(totalElements);

  if (
    (totalPages == null || !Number.isFinite(totalPages)) &&
    totalElements != null &&
    Number.isFinite(totalElements) &&
    pageSize != null &&
    pageSize > 0
  ) {
    totalPages = Math.max(1, Math.ceil(totalElements / pageSize));
  }

  return {
    rows,
    totalPages: Number.isFinite(totalPages) ? totalPages : undefined,
    totalElements: Number.isFinite(totalElements) ? totalElements : undefined,
    last: typeof last === 'boolean' ? last : undefined,
    number: partial.number ?? top.number ?? top.page,
    size: pageSize
  };
}

/**
 * Extrae el array de filas del JSON de listado/búsqueda aunque el backend use distintos “envelopes”
 * (`data.records`, `code`+`data`, `content` tipo Spring, array raíz, etc.).
 */
function unwrapListPayload(payload) {
  if (payload == null || typeof payload !== 'object') {
    return finalizeListPayload(payload, { rows: [] });
  }

  // Respuesta típica `{ code: 'SUCCESS', data: [...], totalPages, totalElements }`: los metadatos van
  // junto al array; antes se perdían al tomar solo `payload.data` como `root` array.
  if (!Array.isArray(payload) && Array.isArray(payload.data)) {
    const hasEnvelopeMeta =
      payload.code === 'SUCCESS' ||
      payload.success === true ||
      payload.totalPages != null ||
      payload.total_pages != null ||
      payload.totalElements != null ||
      payload.total_elements != null ||
      typeof payload.last === 'boolean';
    if (hasEnvelopeMeta) {
      return finalizeListPayload(payload, {
        rows: payload.data,
        totalPages: payload.totalPages ?? payload.total_pages,
        totalElements: payload.totalElements ?? payload.total_elements,
        last: payload.last,
        size: payload.size ?? payload.pageSize,
        number: payload.number ?? payload.page
      });
    }
  }

  const root = payload.data !== undefined && !Array.isArray(payload) ? payload.data : payload;

  if (root?.records?.code === 'SUCCESS' && Array.isArray(root.records.data)) {
    return finalizeListPayload(payload, {
      rows: root.records.data,
      totalPages: root.records.totalPages ?? root.totalPages,
      totalElements: root.records.totalElements ?? root.totalElements,
      last: root.records.last
    });
  }

  if (root?.code === 'SUCCESS' && Array.isArray(root.data)) {
    return finalizeListPayload(payload, {
      rows: root.data,
      totalPages: root.totalPages,
      totalElements: root.totalElements,
      last: root.last,
      size: root.size,
      number: root.number
    });
  }

  if (Array.isArray(root?.content)) {
    return finalizeListPayload(payload, {
      rows: root.content,
      totalPages: root.totalPages,
      totalElements: root.totalElements,
      last: root.last,
      number: root.number,
      size: root.size
    });
  }

  if (Array.isArray(root)) {
    return finalizeListPayload(payload, { rows: root });
  }

  if (Array.isArray(root?.data)) {
    return finalizeListPayload(payload, {
      rows: root.data,
      totalPages: root.totalPages,
      totalElements: root.total ?? root.totalElements,
      last: root.last,
      size: root.size,
      number: root.number
    });
  }

  return finalizeListPayload(payload, { rows: [] });
}

/** Tope de GET encadenados cuando el servidor ignora `size` y devuelve lotes fijos (p. ej. 15 filas). */
const SALES_ORDER_LIST_AGG_MAX_REQUESTS = 120;

/** @param {{ uw: { last?: boolean } }} r */
function mergedSalesOrderListLast(windowStart, rowsLen, uiPageSize, totalElements, r) {
  const te = totalElements != null && Number.isFinite(Number(totalElements)) ? Number(totalElements) : null;
  if (te != null) {
    return windowStart + rowsLen >= te;
  }
  return r.uw.last === true && rowsLen < uiPageSize;
}

/**
 * Si el API devuelve menos filas que `uiPageSize` pero `last === false`, o el número de página del API
 * no coincide con la ventana de la UI (p. ej. tope 15 por petición), encadena GET hasta cubrir el rango
 * solicitado o agotar datos.
 *
 * @param {(apiPage: number, reqSize: number) => Promise<{ uw: object, mapped: object[] }>} doOne
 * @param {number} uiPage - página 1-based como en la UI
 * @param {number} uiPageSize - filas pedidas por página en la UI
 */
async function aggregateSalesOrdersListWindow(doOne, uiPage, uiPageSize) {
  const windowStart = Math.max(0, (uiPage - 1) * uiPageSize);

  const boot = await doOne(uiPage, uiPageSize);
  let chunkSz = Math.max(1, coerceFiniteNumberLoose(boot.uw.size) || boot.mapped.length || 15);
  const startApiPage = Math.floor(windowStart / chunkSz) + 1;
  const skipInitial = windowStart % chunkSz;

  let rows = [];
  let totalElements = boot.uw.totalElements;
  let totalPages = boot.uw.totalPages;
  let apiIterPage = startApiPage;
  let pendingSkip = skipInitial;

  let lastR = boot;
  let iter = 0;

  while (rows.length < uiPageSize && iter < SALES_ORDER_LIST_AGG_MAX_REQUESTS) {
    iter += 1;
    const need = uiPageSize - rows.length;
    const reqSize = Math.min(Math.max(need + pendingSkip, chunkSz), 500);

    let r =
      apiIterPage === uiPage && iter === 1 && uiPage === startApiPage ? boot : await doOne(apiIterPage, reqSize);

    lastR = r;
    chunkSz = Math.max(
      chunkSz,
      coerceFiniteNumberLoose(r.uw.size) || (r.mapped.length > 0 ? r.mapped.length : 0) || chunkSz
    );

    totalElements = r.uw.totalElements ?? totalElements;
    totalPages = r.uw.totalPages ?? totalPages;

    let chunk = r.mapped;
    if (pendingSkip > 0) {
      chunk = chunk.slice(pendingSkip);
      pendingSkip = 0;
    }
    rows.push(...chunk.slice(0, need));

    if (rows.length >= uiPageSize) break;
    if (r.uw.last === true || r.mapped.length === 0) break;
    apiIterPage += 1;
  }

  const out = rows.slice(0, uiPageSize);
  const last = mergedSalesOrderListLast(windowStart, out.length, uiPageSize, totalElements, lastR);

  return {
    rows: out,
    totalPages,
    totalElements,
    last
  };
}

/**
 * GET paginado (solo cabeceras): `sales-orders/headers?page=&size=&sortBy=`
 * Bearer vía `axiosInstance`.
 *
 * @param {{ page?: number, size?: number, sortBy?: string }} params
 * @returns {Promise<{ rows: object[], totalPages?: number, totalElements?: number, last?: boolean }>}
 */
export async function fetchSalesOrdersPage({ page = 1, size = 15, sortBy = 'orderNumber' } = {}) {
  const doOne = async (apiPage, reqSize) => {
    const search = new URLSearchParams();
    search.set('page', String(apiPage));
    search.set('size', String(reqSize));
    search.set('sortBy', sortBy);

    const response = await axiosInstance.get(`sales-orders/headers?${search.toString()}`);
    const uw = unwrapListPayload(response.data);
    const mapped = (uw.rows || []).map(mapSalesOrderApiToRow).filter(Boolean);
    return { uw, mapped };
  };

  return aggregateSalesOrdersListWindow(doOne, page, size);
}

/**
 * GET búsqueda: `sales-orders/search?orderNumber=&status=&channel=&startDate=&endDate=&page=&size=`
 * (`channel`: usar `SALES_ORDER_CHANNEL_UI_TO_API` desde la UI, no el slug del Select).
 * Solo añade query params no vacíos. Bearer vía `axiosInstance`.
 *
 * @param {{ orderNumber?: string, status?: string, channel?: string, startDate?: string, endDate?: string, page?: number, size?: number }} params
 */
export async function fetchSalesOrdersSearch({
  orderNumber,
  status,
  channel,
  startDate,
  endDate,
  page = 1,
  size = 15
} = {}) {
  const baseSearch = new URLSearchParams();

  if (orderNumber != null && String(orderNumber).trim()) {
    baseSearch.set('orderNumber', String(orderNumber).trim());
  }
  if (status != null && String(status).trim()) {
    baseSearch.set('status', String(status).trim());
  }
  if (channel != null && String(channel).trim()) {
    baseSearch.set('channel', String(channel).trim());
  }
  if (startDate != null && String(startDate).trim()) {
    baseSearch.set('startDate', String(startDate).trim());
  }
  if (endDate != null && String(endDate).trim()) {
    baseSearch.set('endDate', String(endDate).trim());
  }

  const doOne = async (apiPage, reqSize) => {
    const search = new URLSearchParams(baseSearch.toString());
    search.set('page', String(apiPage));
    search.set('size', String(reqSize));

    const response = await axiosInstance.get(`sales-orders/search?${search.toString()}`);
    const uw = unwrapListPayload(response.data);
    const mapped = (uw.rows || []).map(mapSalesOrderApiToRow).filter(Boolean);
    return { uw, mapped };
  };

  return aggregateSalesOrdersListWindow(doOne, page, size);
}

/**
 * PUT `sales-orders/{id}` — actualiza el estado de la OV (p. ej. `{ status: "CANCELLED" }`).
 *
 * @param {string|number} id - Identificador recurso (misma clave que selección en listado).
 * @param {string} status - Valor literal esperado por el API.
 * @param {{ signal?: AbortSignal, modifiedBy?: string }} [options]
 */
export async function updateSalesOrderStatus(id, status, options = {}) {
  const key = String(id ?? '').trim();
  if (!key) {
    throw new Error('id vacío');
  }
  const { signal, modifiedBy } = options;
  const cfg = signal ? { signal } : undefined;
  const body = { status };
  const uid = modifiedBy != null ? String(modifiedBy).trim() : '';
  if (uid) {
    body.modifiedBy = uid;
  }
  await axiosInstance.put(`sales-orders/${encodeURIComponent(key)}`, body, cfg);
}

/**
 * PUT `sales-orders/{id}` — solo auditoría `{ modifiedBy }` (p. ej. tras envío exitoso a SAP).
 * No hace petición si `modifiedBy` está vacío.
 *
 * @param {string|number} id
 * @param {string} modifiedBy - UUID usuario
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function updateSalesOrderModifiedBy(id, modifiedBy, options = {}) {
  const key = String(id ?? '').trim();
  if (!key) {
    throw new Error('id vacío');
  }
  const uid = modifiedBy != null ? String(modifiedBy).trim() : '';
  if (!uid) return;
  const { signal } = options;
  const cfg = signal ? { signal } : undefined;
  await axiosInstance.put(`sales-orders/${encodeURIComponent(key)}`, { modifiedBy: uid }, cfg);
}

/**
 * @typedef {{ id: string, outcome: 'ok'|'error', errorMessage?: string }} CancelSalesOrderByIdResult
 */

/**
 * Cancela varias OV con `PUT sales-orders/{id}` y `{ status: "CANCELLED" }` por cada id.
 *
 * **Una por vez, no en paralelo.** Cada cancelación dispara un Cancel en SAP, y SAP devuelve
 * `-2038 ("Internal error occurred")` cuando varias operaciones tocan documentos del mismo cliente
 * al mismo tiempo: cancelando 4 OV juntas, uno fallaba y reintentado solo funcionaba. Serializar
 * evita el bloqueo en origen; el costo son unos segundos más. Un fallo no interrumpe el resto.
 *
 * @param {(string|number)[]} orderIds
 * @param {{ signal?: AbortSignal, modifiedBy?: string }} [options]
 * @returns {Promise<CancelSalesOrderByIdResult[]>}
 */
export async function cancelSalesOrdersByIds(orderIds, options = {}) {
  const raw = Array.isArray(orderIds) ? orderIds : [];
  const ids = [...new Set(raw.map((x) => String(x ?? '').trim()).filter(Boolean))];
  if (ids.length === 0) return [];

  const results = [];
  for (const id of ids) {
    try {
      await updateSalesOrderStatus(id, 'CANCELLED', options);
      results.push({ id, outcome: 'ok' });
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        (typeof data === 'string' && data) ||
        (data && typeof data === 'object' && (data.message || data.error)) ||
        err?.message ||
        'Error';
      results.push({ id, outcome: 'error', errorMessage: String(msg) });
    }
  }
  return results;
}

/**
 * Extrae el objeto orden del envelope HTTP (`code: SUCCESS` + `data`, o `records`, o cuerpo plano).
 *
 * @param {unknown} payload
 * @returns {object|null}
 */
function unwrapDetailPayload(payload) {
  if (payload == null || typeof payload !== 'object') {
    return null;
  }

  const tryEnvelope = (obj) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
    if (obj.records?.code === 'SUCCESS' && obj.records.data != null && typeof obj.records.data === 'object' && !Array.isArray(obj.records.data)) {
      return obj.records.data;
    }
    if (obj.code === 'SUCCESS' && obj.data != null && typeof obj.data === 'object' && !Array.isArray(obj.data)) {
      return obj.data;
    }
    return null;
  };

  const inner = payload.data !== undefined && !Array.isArray(payload) ? payload.data : payload;

  let data = tryEnvelope(inner);
  if (data) return data;

  data = tryEnvelope(payload);
  if (data) return data;

  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    if (
      inner.orderNumber != null ||
      inner.order_number != null ||
      inner.id != null ||
      inner.salesOrderId != null
    ) {
      return inner;
    }
  }

  if (
    payload.orderNumber != null ||
    payload.order_number != null ||
    payload.id != null ||
    payload.salesOrderId != null
  ) {
    return payload;
  }

  return null;
}

/**
 * Primer número finito entre candidatos (montos del detalle OV: `*Amount` en cabecera).
 * @param {...unknown} vals
 * @returns {number|null}
 */
function pickFirstFiniteNumber(...vals) {
  for (const v of vals) {
    if (v == null || v === '') continue;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = parseFloat(String(v).replace(/\s/g, '').replace(',', '.'));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Formato moneda para el detalle OV según la moneda inferida (`inferSalesOrderCurrencyFromMarketplace`):
 * `S/` → soles con 2 decimales; resto → CLP entero. Si el API ya envía string con $, se respeta.
 */
function formatMoneyFromApi(value, currency = 'CLP') {
  if (value == null || value === '') return '—';
  if (typeof value === 'string' && Number.isNaN(parseFloat(String(value).replace(/[^\d.-]/g, ''))) && /[$.]/.test(value)) {
    return value;
  }
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^\d.-]/g, ''));
  if (Number.isNaN(n)) return String(value);
  const sign = n < 0 ? '-' : '';
  if (currency === 'S/') {
    const absSol = Math.round(Math.abs(n) * 100) / 100;
    return `${sign}S/ ${absSol.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  const abs = Math.round(Math.abs(n));
  return `${sign}$${abs.toLocaleString('es-CL')}`;
}

/**
 * Paso del stepper (1..8) cuando el API no envía `workflowCurrentStep`.
 * Orden UI: Creada → Aceptada → Integrada → Bodega → Despachada → En camino → Entregado → Completado.
 * Si el backend numérico aún usa la escala antigua de 6 pasos, debe migrar a 1..8 (p. ej. Despachada = 5).
 */
function workflowStepFromStatus(status) {
  const s = typeof status === 'string' ? status : '';
  const map = {
    Pending: 1,
    Accepted: 2,
    Integrated: 3,
    Integration: 3,
    Warehouse: 4,
    Dispatched: 5,
    InTransit: 6,
    Shipped: 6,
    Delivered: 7,
    Completed: 8,
    CANCELLED: 1,
    Cancelled: 1
  };
  return map[s] ?? 1;
}

function normalizePaymentStatusForDetail(rawPay) {
  const paymentStatus = String(rawPay ?? 'pending').toLowerCase();
  if (['paid', 'pending', 'canceled'].includes(paymentStatus)) return paymentStatus;
  return 'pending';
}

/** Primer texto no vacío (útil para líneas OV cuando el API usa nombres distintos al mock). */
function firstNonEmptyDetailString(...candidates) {
  for (const v of candidates) {
    if (v == null || v === '') continue;
    const s = typeof v === 'string' ? v.trim() : String(v).trim();
    if (s !== '') return s;
  }
  return '';
}

/**
 * SKU / código material en detalle OV: tolera respuestas de import mayorista (Ripley, etc.)
 * donde el código útil viene en `skuCliente`, `materialCode`, o en `product`.
 */
export function pickSalesOrderDetailLineSku(l) {
  if (!l || typeof l !== 'object') return '—';
  const p = l.product && typeof l.product === 'object' ? l.product : null;
  const art = l.articulo && typeof l.articulo === 'object' ? l.articulo : null;
  const artEn = l.article && typeof l.article === 'object' ? l.article : null;
  const sku = firstNonEmptyDetailString(
    l.skuIac,
    l.sku_iac,
    /** Backend OV: código ítem tipo PC13 suele venir en `product_name`. */
    l.product_name,
    l.productName,
    l.sku,
    l.customerSku,
    l.customer_sku,
    l.skuCliente,
    l.sku_cliente,
    l.clientSku,
    l.client_sku,
    l.materialCode,
    l.material_code,
    l.articleCode,
    l.article_code,
    l.codigoArticulo,
    l.codigo_articulo,
    l.codArtRipley,
    l.cod_art_ripley,
    l.codigoRipley,
    l.codigo_ripley,
    l.productSku,
    l.product_sku,
    l.material,
    l.externalSku,
    l.external_sku,
    l.ean,
    l.upc,
    l.barcode,
    l.codigo,
    art?.skuIac,
    art?.sku,
    art?.codigo,
    art?.materialCode,
    art?.codArtRipley,
    art?.cod_art_ripley,
    art?.codigoRipley,
    artEn?.sku,
    artEn?.code,
    p?.skuIac,
    p?.sku_iac,
    p?.product_name,
    p?.productName,
    p?.sku,
    p?.materialCode,
    p?.material_code
  );
  return sku || '—';
}

/**
 * Solo **PC13** para consulta SAP (vista previa «Procesar a SAP»): campos explícitos `pc13`
 * y, si el backend guarda el código en nombre de producto, `product_name` / `productName`
 * en línea o en `product`. Sin `skuIac` ni otros sustitutos: si falta PC13 → `—` para que el
 * fallo sea visible antes de enviar a SAP.
 */
export function pickSalesOrderDetailLineSkuForSapPreview(l) {
  if (!l || typeof l !== 'object') return '—';
  const p = l.product && typeof l.product === 'object' ? l.product : null;
  const pc13Only = firstNonEmptyDetailString(
    l.pc13,
    l.pc_13,
    l.PC13,
    p?.pc13,
    p?.pc_13,
    p?.PC13,
    l.product_name,
    l.productName,
    p?.product_name,
    p?.productName
  );
  return pc13Only || '—';
}

export function pickSalesOrderDetailLineDescription(l) {
  if (!l || typeof l !== 'object') return '—';
  const p = l.product && typeof l.product === 'object' ? l.product : null;
  const art = l.articulo && typeof l.articulo === 'object' ? l.articulo : null;
  const artEn = l.article && typeof l.article === 'object' ? l.article : null;
  const d = firstNonEmptyDetailString(
    l.description,
    l.descripcion,
    l.name,
    l.articleDescription,
    l.article_description,
    l.itemDescription,
    l.item_description,
    l.descripcionArticulo,
    l.descripcion_articulo,
    l.nombreArticulo,
    l.nombre_articulo,
    l.textoArticulo,
    l.texto_articulo,
    art?.descripcion,
    art?.nombre,
    art?.description,
    artEn?.description,
    artEn?.name,
    p?.description,
    p?.name
  );
  return d || '—';
}

/**
 * Destino por tienda a nivel de línea (Perú Ripley/Oechsle: `destinationBranch` / U_sucursal_cliente).
 * @param {object} l
 * @returns {string}
 */
export function pickSalesOrderDetailLineDestinationBranch(l) {
  if (!l || typeof l !== 'object') return '';
  const v = firstNonEmptyDetailString(
    l.destinationBranch,
    l.destination_branch,
    l.u_sucursal_cliente,
    l.uSucursalCliente,
    l.sucursalDestino,
    l.sucursal_destino,
    l.branchDestination,
    l.branch_destination
  );
  return v || '';
}

/**
 * Números del API como objeto (p. ej. BigDecimal serializado) o `{ value: n }`.
 */
function coerceLineMoneyToNumber(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    if ('unscaledValue' in value && 'scale' in value) {
      const u = value.unscaledValue;
      const uv = typeof u === 'bigint' ? Number(u) : typeof u === 'string' ? parseFloat(u) : Number(u);
      if (Number.isNaN(uv)) return null;
      const sc = Number(value.scale ?? 0);
      return uv / 10 ** sc;
    }
    if (value.value != null && typeof value.value !== 'object') {
      return coerceLineMoneyToNumber(value.value);
    }
  }
  return null;
}

/** Une subobjetos habituales donde el backend anida la línea (`line`, `orderLine`, etc.). */
export function mergeDetailLineRow(l) {
  if (!l || typeof l !== 'object') return l;
  const nests = [
    l.line,
    l.orderLine,
    l.order_line,
    l.salesOrderLine,
    l.sales_order_line,
    l.detailLine,
    l.detail_line,
    l.orderDetail,
    l.detail,
    l.item,
    l.salesOrderItem,
    l.sales_order_item
  ].filter((x) => x && typeof x === 'object' && !Array.isArray(x));
  if (nests.length === 0) return l;
  return Object.assign({}, ...nests, l);
}

/**
 * Identificador de línea de OV (SalesOrderItem) desde el objeto crudo del API.
 * @param {object|null|undefined} l
 * @returns {string} UUID o id en string; vacío si no viene en el payload.
 */
export function pickSalesOrderDetailLineItemId(l) {
  const row = mergeDetailLineRow(l);
  const v =
    row.id ??
    row.itemId ??
    row.item_id ??
    row.salesOrderItemId ??
    row.sales_order_item_id ??
    row.orderItemId ??
    row.order_item_id ??
    row.lineItemId ??
    row.line_item_id;
  if (v == null || v === '') return '';
  return typeof v === 'string' ? v.trim() : String(v).trim();
}

/** Busca el primer valor numérico en propiedades cuyo nombre coincide con `keyTest`. */
function scanObjectMoneyByKey(obj, keyTest) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  for (const [k, v] of Object.entries(obj)) {
    if (keyTest(k)) {
      const n = parseLineMoneyNumber(v);
      if (n != null && Number.isFinite(n)) return n;
    }
  }
  return null;
}

const UNIT_PRICE_KEY_RE =
  /^(pricePerUnit|price_per_unit|unitPrice|unit_price|unitPriceAmount|precioUnitario|precio_unitario|precioAcordado|precio_acordado|netPrice|net_price|price|listPrice|list_price|precioLista|precio_lista|valorUnitario|valor_unitario|unitValue|unit_value|rate|kbetr|condValue|netpr|unitAmount|priceAmount)$/i;
const LINE_TOTAL_KEY_RE =
  /^(lineTotal|line_total|totalLine|total_line|importe|importeLinea|importe_linea|totalLinea|total_linea|extendedPrice|lineAmount|line_amount|subtotal|montoLinea|monto_linea|valorLinea|valor_linea|amount|totalPrecio|importeTotal|importe_total|netValue|grossValue|netwr|lineValue|grandTotal|totalAmount)$/i;

/**
 * Montos en líneas OV: tolera miles con punto/coma (misma idea que `parseMoneyNumber` en bulk preview).
 */
function parseLineMoneyNumber(value) {
  if (value === '' || value == null) return null;
  const coerced = coerceLineMoneyToNumber(value);
  if (coerced != null && Number.isFinite(coerced)) return coerced;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const raw = String(value)
    .trim()
    .replace(/\$/g, '')
    .replace(/CLP/gi, '')
    .replace(/\s/g, '');
  if (!raw) return null;
  if (/^\d{1,3}(,\d{3})+$/.test(raw)) {
    const n = parseFloat(raw.replace(/,/g, ''), 10);
    return Number.isNaN(n) ? null : n;
  }
  if (/^\d{1,3}(\.\d{3})+$/.test(raw)) {
    const n = parseFloat(raw.replace(/\./g, ''), 10);
    return Number.isNaN(n) ? null : n;
  }
  const s = raw
    .replace(/\./g, '')
    .replace(/,/g, '.')
    .replace(/[^\d.-]/g, '');
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

/** Primer valor numérico usable para precio / total línea (Paris, SAP, Falabella, etc.). */
function firstNumericLineValue(...candidates) {
  for (const v of candidates) {
    if (v == null || v === '') continue;
    const n = parseLineMoneyNumber(v);
    if (n != null && Number.isFinite(n)) return n;
  }
  return null;
}

function pickDetailLineUnitPriceRaw(l) {
  if (!l || typeof l !== 'object') return null;
  const row = mergeDetailLineRow(l);
  const p = row.product && typeof row.product === 'object' ? row.product : null;
  const pr = row.pricing && typeof row.pricing === 'object' ? row.pricing : null;
  const art = row.articulo && typeof row.articulo === 'object' ? row.articulo : null;
  const artEn = row.article && typeof row.article === 'object' ? row.article : null;
  const mat = row.material && typeof row.material === 'object' ? row.material : null;
  const vals = firstNumericLineValue(
    row.pricePerUnit,
    row.price_per_unit,
    row.unitPrice,
    row.unit_price,
    row.precioUnitario,
    row.precio_unitario,
    row.precioAcordado,
    row.precio_acordado,
    row.agreedPrice,
    row.agreed_price,
    row.precioUnitarioNeto,
    row.precio_unitario_neto,
    row.precioUnitarioBruto,
    row.precio_unitario_bruto,
    row.price,
    row.precio,
    row.unitPriceNet,
    row.unit_price_net,
    row.valorUnitario,
    row.valor_unitario,
    row.unitValue,
    row.unit_value,
    row.listPrice,
    row.list_price,
    row.precioLista,
    row.precio_lista,
    row.precioListaSap,
    row.precio_lista_sap,
    row.netPrice,
    row.net_price,
    row.precioNeto,
    row.precio_neto,
    row.amountUnit,
    row.amount_unit,
    row.costoUnitario,
    row.costo_unitario,
    row.valores?.precioUnitario,
    row.valores?.precioAcordado,
    row.valores?.unitPrice,
    pr?.unitPrice,
    pr?.unit_price,
    pr?.precioUnitario,
    pr?.precioAcordado,
    p?.unitPrice,
    p?.precioUnitario,
    p?.price,
    art?.precioUnitario,
    art?.precioAcordado,
    art?.unitPrice,
    art?.precioLista,
    artEn?.unitPrice,
    artEn?.price,
    artEn?.listPrice,
    artEn?.netPrice,
    mat?.unitPrice,
    mat?.price
  );
  if (vals != null) return vals;
  let s = scanObjectMoneyByKey(row, (k) => UNIT_PRICE_KEY_RE.test(k));
  if (s != null) return s;
  for (const nest of [pr, p, art, artEn, mat, row.valores]) {
    s = scanObjectMoneyByKey(nest, (k) => UNIT_PRICE_KEY_RE.test(k));
    if (s != null) return s;
  }
  return null;
}

function pickDetailLineTotalRaw(l) {
  if (!l || typeof l !== 'object') return null;
  const row = mergeDetailLineRow(l);
  const p = row.product && typeof row.product === 'object' ? row.product : null;
  const pr = row.pricing && typeof row.pricing === 'object' ? row.pricing : null;
  const art = row.articulo && typeof row.articulo === 'object' ? row.articulo : null;
  const artEn = row.article && typeof row.article === 'object' ? row.article : null;
  const vals = firstNumericLineValue(
    row.lineTotal,
    row.line_total,
    row.totalLine,
    row.total_line,
    row.lineAmount,
    row.line_amount,
    row.importe,
    row.importeLinea,
    row.importe_linea,
    row.importeTotal,
    row.importe_total,
    row.precioTotalLinea,
    row.precio_total_linea,
    row.subtotal,
    row.montoLinea,
    row.monto_linea,
    row.valorLinea,
    row.valor_linea,
    row.totalLinea,
    row.total_linea,
    row.totalPrecio,
    row.total_precio,
    row.extendedPrice,
    row.extended_price,
    row.amount,
    row.valores?.importe,
    row.valores?.importeLinea,
    row.valores?.totalLine,
    row.valores?.totalLinea,
    pr?.lineTotal,
    pr?.total,
    pr?.importe,
    p?.lineTotal,
    art?.importe,
    art?.totalLinea,
    artEn?.lineTotal,
    artEn?.totalPrice,
    artEn?.total
  );
  if (vals != null) return vals;
  let s = scanObjectMoneyByKey(row, (k) => LINE_TOTAL_KEY_RE.test(k));
  if (s != null) return s;
  for (const nest of [pr, p, art, artEn, row.valores]) {
    s = scanObjectMoneyByKey(nest, (k) => LINE_TOTAL_KEY_RE.test(k));
    if (s != null) return s;
  }
  return null;
}

/**
 * Array de líneas en GET detalle: nombres habituales + anidado en `header` (mayoristas).
 * Exportado para vista previa «Procesar a SAP» (Chile).
 */
export function pickSalesOrderDetailLinesArray(api) {
  if (!api || typeof api !== 'object') return [];
  const headerBlock = api.header && typeof api.header === 'object' ? api.header : null;
  const bodyBlock = api.body && typeof api.body === 'object' ? api.body : null;
  const detailBlock = api.detail && typeof api.detail === 'object' ? api.detail : null;
  const salesOrderNested = api.salesOrder && typeof api.salesOrder === 'object' ? api.salesOrder : null;
  const candidates = [
    api.lines,
    api.items,
    api.orderLines,
    api.order_lines,
    api.details,
    api.lineItems,
    api.line_items,
    api.salesOrderLines,
    api.sales_order_lines,
    api.orderDetails,
    api.order_details,
    api.lineas,
    api.detalleLineas,
    api.detalle_lineas,
    api.orderLineList,
    api.order_line_list,
    api.orderLineItems,
    api.order_line_items,
    headerBlock?.lines,
    headerBlock?.items,
    headerBlock?.orderLines,
    headerBlock?.order_lines,
    bodyBlock?.lines,
    bodyBlock?.lineas,
    bodyBlock?.orderLines,
    bodyBlock?.order_lines,
    bodyBlock?.items,
    detailBlock?.lines,
    detailBlock?.lineas,
    salesOrderNested?.lines,
    salesOrderNested?.items,
    salesOrderNested?.orderLines,
    salesOrderNested?.order_lines,
    salesOrderNested?.lineas
  ];
  for (const c of candidates) {
    if (Array.isArray(c) && c.length > 0) return c;
  }
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
}

/**
 * Cantidad, precio unitario y total de línea en números (mismo criterio que el detalle UI).
 * @param {object|null|undefined} l
 * @returns {{ quantity: number, unitPrice: number|null, lineTotal: number|null }}
 */
export function getSalesOrderDetailLineNumericFields(l) {
  if (!l || typeof l !== 'object') {
    return { quantity: 0, unitPrice: null, lineTotal: null };
  }
  const qRaw = l.quantity ?? l.qty ?? l.cantidad ?? l.cantidadPedida ?? l.orderedQuantity ?? l.ordered_quantity ?? 0;
  const q =
    typeof qRaw === 'number' && Number.isFinite(qRaw)
      ? qRaw
      : parseFloat(String(qRaw).replace(',', '.')) || 0;
  let unitNum = pickDetailLineUnitPriceRaw(l);
  let lineTotNum = pickDetailLineTotalRaw(l);
  if (lineTotNum == null && unitNum != null && q > 0) {
    lineTotNum = unitNum * q;
  }
  if (unitNum == null && lineTotNum != null && q > 0) {
    unitNum = lineTotNum / q;
  }
  return {
    quantity: q,
    unitPrice: unitNum != null && Number.isFinite(unitNum) ? unitNum : null,
    lineTotal: lineTotNum != null && Number.isFinite(lineTotNum) ? lineTotNum : null
  };
}

/**
 * GET `sales-orders/order-number/{orderNumber}` (segmento codificado con `encodeURIComponent`).
 * Bearer vía `axiosInstance`.
 *
 * @param {string|number} orderNumber
 * @returns {Promise<object>} Cuerpo crudo de la orden (antes del mapeo UI).
 */
export async function fetchSalesOrderByOrderNumber(orderNumber) {
  const key = orderNumber != null ? String(orderNumber).trim() : '';
  if (!key) {
    throw new Error('Número de orden requerido');
  }
  const encoded = encodeURIComponent(key);
  const response = await axiosInstance.get(`sales-orders/order-number/${encoded}`);
  const data = unwrapDetailPayload(response.data);
  if (!data || typeof data !== 'object') {
    throw new Error('Respuesta de detalle de OV sin datos válidos');
  }
  return data;
}

/**
 * GET `sales-orders/{id}` — detalle por **id de OV** (misma clave que `PUT sales-orders/{id}` / selección en listado).
 * Preferir esta función para vista previa SAP y operaciones por recurso.
 *
 * @param {string|number} salesOrderId
 * @returns {Promise<object>} Cuerpo crudo de la orden (antes del mapeo UI).
 */
export async function fetchSalesOrderById(salesOrderId) {
  const key = salesOrderId != null ? String(salesOrderId).trim() : '';
  if (!key) {
    throw new Error('Id de orden requerido');
  }
  const encoded = encodeURIComponent(key);
  const response = await axiosInstance.get(`sales-orders/${encoded}`);
  const data = unwrapDetailPayload(response.data);
  if (!data || typeof data !== 'object') {
    throw new Error('Respuesta de detalle de OV sin datos válidos');
  }
  return data;
}

/**
 * Convierte la respuesta del API al shape que consume `SalesOrderDetail.js` (como `OV_DETAIL` en mock).
 *
 * **Backend útil:** `orderNumber` o `order_number`, y a ser posible `status`, `lines`/`items`/`orderLines`/`details` (o líneas bajo `header`), `client`/`customer`, `shipping`/`delivery`.
 * **Resumen financiero:** cabecera `subtotalAmount`, `taxAmount`, `totalAmount`, `paidAmount`. La fila «Descuento» en UI está desactivada; el API puede seguir enviando `discountAmount` (se expone `discountPercent` por compatibilidad, sin mostrarlo en resumen).
 * **Import mayorista (Ripley, etc.):** las líneas pueden traer `skuCliente`, `materialCode`, `descripcion`, `precio`, etc.; se mapean al mismo shape de tabla.
 * **Defaults en front:** `lines` y `trackingEvents` → `[]`, `workflowCurrentStep` desde `status` o `1`, textos vacíos → `'—'`, `incoterm` → `'FOB'`.
 * **`externalOrderId`:** mismo criterio que en listado; en detalle alimenta stepper (paso 2 + etiqueta) y tarjeta Estado.
 *
 * @param {object|null|undefined} api
 * @returns {object|null}
 */
export function mapSalesOrderDetailApiToUi(api) {
  if (!api || typeof api !== 'object') return null;

  const header = api.header && typeof api.header === 'object' ? api.header : null;
  const meta = api.meta && typeof api.meta === 'object' ? api.meta : null;

  const id = api.id ?? api.salesOrderId ?? api.orderId ?? api.orderNumber ?? api.order_number ?? '';
  const orderNumber = api.orderNumber ?? api.order_number ?? String(id);

  const rawStatus = api.status ?? api.orderStatus ?? api.order_status ?? 'Pending';
  const status = typeof rawStatus === 'string' ? rawStatus : String(rawStatus);
  const statusLabel = api.statusLabel ?? api.statusDescription ?? api.status_label ?? status;

  const pay = normalizePaymentStatusForDetail(api.paymentStatus ?? api.payment_status);
  const paymentStatusLabel =
    api.paymentStatusLabel ??
    api.payment_status_label ??
    (pay === 'paid' ? 'Pagado' : pay === 'canceled' ? 'Anulado' : 'Pendiente');

  const marketplace = api.marketplace ?? api.marketPlace ?? '';
  let marketplaceLabel =
    api.marketplaceLabel ?? api.marketplaceName ?? api.marketplace_label ?? marketplace;
  if (
    String(marketplace).trim().toUpperCase() === 'IXCOMERCIOPERU' &&
    (!String(marketplaceLabel || '').trim() || String(marketplaceLabel).trim().toUpperCase() === 'IXCOMERCIOPERU')
  ) {
    marketplaceLabel = 'IX Comercio Perú';
  }
  /** Montos del detalle en la moneda de la OV (S/ con 2 decimales en Perú, CLP entero en Chile). */
  const detailCurrency = inferSalesOrderCurrencyFromMarketplace(marketplace, marketplaceLabel, api.currency);
  const formatClMoneyFromApi = (value) => formatMoneyFromApi(value, detailCurrency);

  const channel = api.channel ?? api.salesChannel ?? api.sales_channel ?? '';
  const clientNameTop = api.clientName ?? api.client_name ?? api.customerName ?? api.customer_name ?? '';
  const clientRaw = api.client ?? api.customer ?? {};
  const companyName =
    clientRaw.companyName ?? clientRaw.company_name ?? clientNameTop ?? '';
  /** Mismos alias que `mapSalesOrderApiToRow` (listado), más `header` / `meta`. */
  const rut =
    clientRaw.rut ??
    clientRaw.taxId ??
    clientRaw.tax_id ??
    api.clientRut ??
    api.client_rut ??
    api.customerRut ??
    api.customer_rut ??
    header?.clientRut ??
    header?.client_rut ??
    meta?.clientRut ??
    '';

  const channelLabel = channel ? `Canal ${channel}` : '';
  const mpLabel = marketplaceLabel ? String(marketplaceLabel) : '';
  const headerSubtitleParts = [companyName, channelLabel, mpLabel].filter(Boolean);
  const headerSubtitle =
    api.headerSubtitle ??
    api.header_subtitle ??
    (headerSubtitleParts.length ? headerSubtitleParts.join(' - ') : '—');

  const trackingNum = api.trackingNumber ?? api.tracking_number ?? api.tracking?.number ?? '';
  const trackingButtonText =
    api.trackingButtonText ??
    api.tracking_button_text ??
    (trackingNum ? `Tracking N° ${trackingNum}` : '');

  const shipRaw = api.shipping ?? api.delivery ?? {};
  const shipTrack = shipRaw.tracking ?? shipRaw.trackingNumber ?? trackingNum ?? '—';

  const linesRaw = pickSalesOrderDetailLinesArray(api);
  const lines = linesRaw.map((l, idx) => {
    const qRaw = l.quantity ?? l.qty ?? l.cantidad ?? l.cantidadPedida ?? l.orderedQuantity ?? l.ordered_quantity ?? 0;
    const q =
      typeof qRaw === 'number' && Number.isFinite(qRaw)
        ? qRaw
        : parseFloat(String(qRaw).replace(',', '.')) || 0;
    let unitNum = pickDetailLineUnitPriceRaw(l);
    let lineTotNum = pickDetailLineTotalRaw(l);
    if (lineTotNum == null && unitNum != null && q > 0) {
      lineTotNum = unitNum * q;
    }
    if (unitNum == null && lineTotNum != null && q > 0) {
      unitNum = lineTotNum / q;
    }
    const disc = l.discount ?? l.descuento ?? null;

    const shippedQty = parseSalesOrderDetailShipReturnQty(
      l.shippedQuantity ?? l.shipped_quantity
    );
    const returnedQty = parseSalesOrderDetailShipReturnQty(
      l.returnedQuantity ?? l.returned_quantity
    );
    const lineMerged = mergeDetailLineRow(l);
    const brokeQty = parseSalesOrderDetailBrokeQty(
      lineMerged.brokeQuantity ??
        lineMerged.broke_quantity ??
        lineMerged.brokeQty ??
        lineMerged.broke_qty
    );

    return {
      lineNo: l.lineNo ?? l.line_no ?? l.lineNumber ?? l.line_number ?? l.position ?? idx + 1,
      skuIac: pickSalesOrderDetailLineSku(l),
      description: pickSalesOrderDetailLineDescription(l),
      destinationBranch: pickSalesOrderDetailLineDestinationBranch(l),
      quantity: q,
      shippedQuantity: shippedQty,
      returnedQuantity: returnedQty,
      brokeQuantity: brokeQty,
      unitPrice:
        l.unitPriceFormatted ??
        l.unit_price_formatted ??
        (unitNum != null ? formatClMoneyFromApi(unitNum) : '—'),
      discount:
        disc != null && disc !== ''
          ? typeof disc === 'number'
            ? formatClMoneyFromApi(disc)
            : String(disc)
          : null,
      lineTotal:
        l.lineTotalFormatted ??
        l.line_total_formatted ??
        (lineTotNum != null ? formatClMoneyFromApi(lineTotNum) : '—'),
      lineStatus: l.lineStatus ?? l.line_status ?? l.status ?? '—'
    };
  });

  const financeRaw = api.finance ?? {};
  const totalVal = api.total ?? api.totalAmount ?? api.total_amount;
  const totalFormatted = api.totalFormatted ?? api.total_formatted ?? formatClMoneyFromApi(totalVal);

  /** Montos numéricos del endpoint de detalle (`subtotalAmount`, `taxAmount`, etc.). */
  const subtotalAmountNum = pickFirstFiniteNumber(
    api.subtotalAmount,
    api.subtotal_amount,
    financeRaw.subtotalAmount,
    financeRaw.subtotal_amount,
    financeRaw.subtotal,
    api.subtotal
  );
  const taxAmountNum = pickFirstFiniteNumber(
    api.taxAmount,
    api.tax_amount,
    financeRaw.taxAmount,
    financeRaw.tax_amount,
    financeRaw.tax,
    api.tax,
    api.iva
  );
  const totalAmountNum = pickFirstFiniteNumber(
    api.totalAmount,
    api.total_amount,
    financeRaw.totalAmount,
    financeRaw.total_amount,
    api.total,
    financeRaw.total,
    totalVal
  );
  const paidAmountNum = pickFirstFiniteNumber(
    api.paidAmount,
    api.paid_amount,
    financeRaw.paidAmount,
    financeRaw.paid_amount
  );

  /** Porcentaje SAP / import (alias); `discountAmount` en GET suele ser el valor porcentual (cabecera import). */
  const rawDiscountPercent =
    financeRaw.discountPercent ??
    financeRaw.discount_percent ??
    api.discountPercent ??
    api.discount_percent ??
    financeRaw.discountAmount ??
    api.discountAmount;
  let discountPercent = null;
  if (rawDiscountPercent != null && rawDiscountPercent !== '') {
    const n =
      typeof rawDiscountPercent === 'number' && Number.isFinite(rawDiscountPercent)
        ? rawDiscountPercent
        : parseFloat(String(rawDiscountPercent).replace(/\s/g, '').replace(',', '.'));
    if (Number.isFinite(n) && n >= 0) {
      discountPercent = n;
    }
  }

  /*
   * Resumen financiero — descuento desactivado en pantalla: no se arma texto de descuento.
   * Si se vuelve a mostrar, recomponer desde `discountPercent`, `discountTotal`, etc.
   */

  const finance = {
    subtotalNeto:
      subtotalAmountNum != null
        ? formatClMoneyFromApi(subtotalAmountNum)
        : financeRaw.subtotalNeto ??
          financeRaw.subtotal_neto ??
          formatClMoneyFromApi(financeRaw.subtotal ?? api.subtotal),
    /** Porcentaje numérico del API (p. ej. descuento SAP); no se muestra en el resumen financiero actual. */
    discountPercent,
    /** Columna descuento desconectada en UI; se deja en «—». */
    discount: '—',
    iva:
      taxAmountNum != null
        ? formatClMoneyFromApi(taxAmountNum)
        : financeRaw.iva != null
          ? String(financeRaw.iva)
          : formatClMoneyFromApi(api.tax ?? api.iva),
    total:
      totalAmountNum != null
        ? formatClMoneyFromApi(totalAmountNum)
        : financeRaw.total != null
          ? String(financeRaw.total)
          : totalFormatted,
    paid:
      paidAmountNum != null
        ? formatClMoneyFromApi(paidAmountNum)
        : financeRaw.paid != null
          ? String(financeRaw.paid)
          : pay === 'paid'
            ? totalFormatted
            : '—',
    balance: financeRaw.balance != null ? String(financeRaw.balance) : pay === 'paid' ? '$0' : totalFormatted
  };

  const trackingEventsRaw = api.trackingEvents ?? api.tracking_events ?? [];
  const trackingEvents = Array.isArray(trackingEventsRaw)
    ? trackingEventsRaw.map((ev) => ({
        state: ev.state ?? 'pending',
        datetime: ev.datetime ?? ev.dateTime ?? ev.at ?? null,
        title: ev.title ?? ev.label ?? '—',
        description: ev.description ?? ev.detail ?? '',
        location: ev.location ?? ev.place ?? ''
      }))
    : [];

  const workflowCurrentStep =
    typeof api.workflowCurrentStep === 'number'
      ? api.workflowCurrentStep
      : typeof api.workflow_current_step === 'number'
        ? api.workflow_current_step
        : workflowStepFromStatus(status);

  /** Igual que la columna «Fecha de la OC» del listado (`mapSalesOrderApiToRow.purchaseOrderDate`); si no hay, fechas de creación. */
  const dateOcOrListEquivalentRaw =
    firstNonEmptyDate(
      api.shippedDate,
      api.shipped_date,
      api.ShippedDate,
      header?.shippedDate,
      header?.shipped_date,
      meta?.shippedDate,
      api.purchaseOrderDate,
      api.purchase_order_date,
      api.orderDate,
      api.order_date,
      api.poDate,
      api.po_date,
      api.customerOrderDate,
      api.customer_order_date,
      api.fechaOc,
      api.fecha_oc,
      api.fechaOC,
      api.fechaPedido,
      api.fecha_pedido,
      api.ocDate,
      api.oc_date,
      api.documentDate,
      api.document_date,
      header?.purchaseOrderDate,
      header?.orderDate,
      header?.fechaOc,
      meta?.purchaseOrderDate,
      meta?.orderDate
    ) ||
    firstNonEmptyDate(
      api.createdAt,
      api.created_at,
      api.creationDate,
      api.creation_date,
      api.fechaCreacion,
      api.fecha_creacion,
      api.fechaRegistro,
      api.fecha_registro,
      api.registeredAt,
      api.registered_at,
      api.dateCreated,
      api.date_created,
      header?.createdAt,
      header?.fechaCreacion,
      meta?.createdAt
    );

  const externalOrderId = pickExternalOrderId(api, header, meta);
  const docNumberReference = pickDocNumberReference(api, header, meta);

  const estimatedDeliveryRaw = firstNonEmptyDate(
    api.estimatedDeliveryDate,
    api.estimated_delivery_date,
    api.deliveryDate,
    api.delivery_date,
    api.fechaEntrega,
    api.fecha_entrega,
    api.fechaEntregaEstimada,
    api.fecha_entrega_estimada,
    api.fechaEntregaEst,
    api.shipDate,
    api.ship_date,
    api.expectedDeliveryDate,
    api.expected_delivery_date,
    header?.estimatedDeliveryDate,
    header?.fechaEntrega,
    meta?.estimatedDeliveryDate
  );

  const totalUnitsByOcReference = parseTotalUnitsByOcReferenceFromApi(api, header, meta, shipRaw);

  return {
    id: String(id || orderNumber),
    orderNumber: String(orderNumber),
    headerCode: api.headerCode ?? api.header_code ?? orderNumber ?? String(id),
    headerSubtitle,
    trackingButtonText,
    trackingNumber: trackingNum || shipTrack,
    incoterm: api.incoterm ?? 'FOB',
    workflowCurrentStep,

    createdAt: formatDetailDateCl(dateOcOrListEquivalentRaw),
    estimatedDeliveryDate: estimatedDeliveryRaw ? formatDetailDateCl(estimatedDeliveryRaw) : '',
    channel,
    marketplace: String(marketplace).toLowerCase(),
    marketplaceLabel: String(marketplaceLabel || '—'),
    clientName: clientNameTop || companyName,
    clientRut: rut,
    total: api.total != null ? String(api.total) : totalFormatted,
    totalFormatted,
    currency: inferSalesOrderCurrencyFromMarketplace(marketplace, marketplaceLabel, api.currency),
    shippingCity: api.shippingCity ?? api.shipping_city ?? shipRaw.city ?? '',
    paymentStatus: pay,
    paymentStatusLabel,
    status,
    statusLabel,
    externalOrderId,
    docNumberReference,

    client: {
      companyName,
      rut,
      email: clientRaw.email ?? api.clientEmail ?? '',
      phone: clientRaw.phone ?? api.phone ?? '',
      billingAddress: clientRaw.billingAddress ?? clientRaw.billing_address ?? api.billingAddress ?? ''
    },
    shipping: {
      address: shipRaw.address ?? shipRaw.street ?? '',
      city: shipRaw.city ?? '',
      method: shipRaw.method ?? shipRaw.shippingMethod ?? '',
      tracking: shipTrack === '—' ? '' : shipTrack,
      shippingCostFormatted:
        shipRaw.shippingCostFormatted ??
        shipRaw.shipping_cost_formatted ??
        formatClMoneyFromApi(shipRaw.shippingCost ?? shipRaw.shipping_cost)
    },
    trackingLastUpdate: api.trackingLastUpdate ?? api.tracking_last_update ?? '',
    trackingEvents,
    lines,
    finance,
    /** Total unidades de la OC de referencia si el API lo envía; si no, el detalle puede calcularlo vía `fetchTotalUnitsAcrossOcReference`. */
    totalUnitsByOcReference
  };
}

const MAX_OC_SIBLING_SEARCH_PAGES = 30;

/**
 * Suma las cantidades de línea de **todas** las OV que comparten la misma OC de referencia (prefijo de
 * `orderNumber` antes del último `-`, ver `orderNumberOcPrefix`). Incluye siempre la OV actual.
 *
 * Usa `GET sales-orders/search?orderNumber={prefijo}` (paginado) y un detalle por número distinto encontrado.
 * Si el backend expone el total en detalle, preferir `totalUnitsByOcReference` del mapeo y no llamar a esta función.
 *
 * @param {string|number|undefined|null} orderNumber - Número de OV negocio (ej. `417098-100`).
 * @returns {Promise<number>}
 */
export async function fetchTotalUnitsAcrossOcReference(orderNumber) {
  const key = orderNumber != null ? String(orderNumber).trim() : '';
  if (!key) return 0;

  const orderNumbersToFetch = new Set([key]);
  const prefix = orderNumberOcPrefix(key);

  if (prefix) {
    const pageSize = 100;
    for (let page = 1; page <= MAX_OC_SIBLING_SEARCH_PAGES; page++) {
      const { rows, totalPages, last } = await fetchSalesOrdersSearch({
        orderNumber: prefix,
        page,
        size: pageSize
      });
      for (const row of rows || []) {
        const on = String(row.orderNumber ?? '').trim();
        if (!on) continue;
        if (orderNumberOcPrefix(on) === prefix) {
          orderNumbersToFetch.add(on);
        }
      }
      if (last === true) break;
      if (typeof totalPages === 'number' && page >= totalPages) break;
      if (!rows || rows.length < pageSize) break;
    }
  }

  let total = 0;
  for (const on of orderNumbersToFetch) {
    try {
      const raw = await fetchSalesOrderByOrderNumber(on);
      const mapped = mapSalesOrderDetailApiToUi(raw);
      total += sumSalesOrderDetailLineQuantities(mapped?.lines);
    } catch {
      /* ignorar OV hermana no disponible */
    }
  }
  return total;
}
